import React, { useState, useEffect } from "react";
import {
  Table,
  Badge,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import paginationFactory from "react-bootstrap-table2-paginator";
import { DASHBOARD, API_TIMEOUT } from "../../../utils";
import axios from "axios";
import { numberWithCommas } from "../../../utils/func";

const { SearchBar } = Search;

/**
 * Komponen Tabel untuk menampilkan rekapan obat per puskesmas
 * @param {Array} data - Data rekapan (opsional, jika diberikan akan digunakan langsung)
 * @param {number} selectedYear - Tahun yang dipilih (opsional)
 */
export default function RekapanPuskesmasTable({
  data: propData,
  selectedYear: propYear,
}) {
  const [rekapanData, setRekapanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(
    propYear || new Date().getFullYear()
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);

  // Update selectedYear jika propYear berubah
  useEffect(() => {
    if (propYear !== undefined) {
      setSelectedYear(propYear);
    }
  }, [propYear]);

  useEffect(() => {
    if (propData && propData.length >= 0) {
      // Jika data diberikan dari parent, gunakan data tersebut
      const formattedData = propData.map((item, index) => ({
        ...item,
        No: index + 1,
      }));
      setRekapanData(formattedData);
      setLoading(false);
    } else if (!propData) {
      // Jika tidak ada data, fetch sendiri
      fetchRekapanData();
    }
  }, [propData]);

  // Fetch data jika tahun berubah dan tidak ada propData
  useEffect(() => {
    if (!propData) {
      fetchRekapanData();
    }
  }, [selectedYear]);

  const fetchRekapanData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${DASHBOARD}ambil/rekap/${selectedYear}`,
        {
          timeout: API_TIMEOUT,
        }
      );

      if (response.data.success) {
        const data = response.data.data.map((item, index) => ({
          ...item,
          No: index + 1,
        }));
        setRekapanData(data);
      }
    } catch (error) {
      console.error("Error fetching rekapan data:", error);
    } finally {
      setLoading(false);
    }
  };

  const changeSelectDropdownSimple = (e) => {
    const select = e.currentTarget.textContent;
    setSelectedYear(parseInt(select));
  };

  const currentYear = new Date().getFullYear();
  const listTahun = [];
  for (let tahun = 2018; tahun <= currentYear; tahun++) {
    listTahun.push({ tahun });
  }

  const handleTableChange = (type, { page, sizePerPage }) => {
    if (type === "pagination") {
      setCurrentPage(page);
      setSizePerPage(sizePerPage);
    }
  };

  const columns = [
    {
      dataField: "No",
      text: "No",
      sort: true,
      width: 60,
      headerStyle: { textAlign: "center" },
      style: { textAlign: "center" },
    },
    {
      dataField: "lokasi",
      text: "Puskesmas",
      sort: true,
    },
    {
      dataField: "total_masuk",
      text: "Total Masuk",
      sort: true,
      formatter: (cell) => numberWithCommas(cell || 0),
      headerStyle: { textAlign: "right" },
      style: { textAlign: "right" },
    },
    {
      dataField: "total_keluar",
      text: "Total Keluar",
      sort: true,
      formatter: (cell) => numberWithCommas(cell || 0),
      headerStyle: { textAlign: "right" },
      style: { textAlign: "right" },
    },
    {
      dataField: "stok",
      text: "Stok",
      sort: true,
      formatter: (cell) => numberWithCommas(cell || 0),
      headerStyle: { textAlign: "right" },
      style: { textAlign: "right" },
    },
    {
      dataField: "stok_ready",
      text: "Stok Ready",
      sort: true,
      formatter: (cell) => numberWithCommas(cell || 0),
      headerStyle: { textAlign: "right" },
      style: { textAlign: "right" },
    },
    {
      dataField: "stok_expired",
      text: "Stok Expired",
      sort: true,
      formatter: (cell) => (
        <span style={{ color: cell > 0 ? "red" : "inherit" }}>
          {numberWithCommas(cell || 0)}
        </span>
      ),
      headerStyle: { textAlign: "right" },
      style: { textAlign: "right" },
    },
  ];

  if (loading) {
    return <p>Memuat data rekapan...</p>;
  }

  // Hanya tampilkan dropdown tahun jika tidak ada propData (tabel mandiri)
  const showYearDropdown = !propData;

  return (
    <div>
      {showYearDropdown && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-0">
              Rekapan Obat per Puskesmas - Tahun {selectedYear}
            </h6>
          </div>
          <UncontrolledButtonDropdown>
            <DropdownToggle caret color="primary" size="sm">
              {selectedYear}
            </DropdownToggle>
            <DropdownMenu>
              {listTahun.map((item) => (
                <DropdownItem
                  key={item.tahun}
                  onClick={changeSelectDropdownSimple}
                >
                  {item.tahun}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </div>
      )}

      <ToolkitProvider
        columns={columns}
        data={rekapanData}
        keyField="id"
        search
      >
        {(props) => (
          <div>
            <div className="mb-2">
              <SearchBar
                {...props.searchProps}
                placeholder="Cari puskesmas..."
              />
            </div>
            <BootstrapTable
              bordered={false}
              classes="table-striped table-hover"
              pagination={paginationFactory({
                page: currentPage,
                sizePerPage: sizePerPage,
                showTotal: true,
                paginationTotalRenderer: (from, to, size) => (
                  <span>
                    Menampilkan {from} sampai {to} dari {size} data
                  </span>
                ),
              })}
              onTableChange={handleTableChange}
              {...props.baseProps}
            />
          </div>
        )}
      </ToolkitProvider>
    </div>
  );
}
